import React, { useCallback, useState } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from 'src/components/Table/TableCell';
import PropTypes from 'prop-types';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MoreMenu from 'src/components/MoreMenu/MoreMenu';
import { Chip } from '@mui/material';
import StatusIcon from 'src/components/StatusIcon';
import IconStatusAndColor from 'src/utils/constants/common';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import StoreIcon from '@mui/icons-material/Store';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ReconciliationStatus from './reconciliationStatus.enum';
import ReconcilePaymentModal from './ReconcilePaymentModal';
import CreateUpfrontRentModal from './CreateUpfrontRentModal';
import CreateCapitalContributionModal from './CreateCapitalContributionModal';

const MenuOptions = {
  RECONCILE: 'RECONCILE',
  CREATE_UPFRONT_RENT: 'CREATE_UPFRONT_RENT',
  CREATE_CAPITAL_CONTRIBUTION: 'CREATE_CAPITAL_CONTRIBUTION'
};

const SantanderPaymentDetailsRow = ({
  id,
  client,
  amount,
  incomeClabe,
  paymentMethod,
  unreconciledPayments,
  reconciledAmount,
  unreconciledAmount,
  status,
  reportId,
  allPayments
}) => {
  const { t } = useTranslation();
  const [reconcileModalOpen, setReconcileModalOpen] = useState(false);
  const [upfrontRentModalOpen, setUpfrontRentModalOpen] = useState(false);
  const [createContributionModalOpen, setCreateContributionModalOpen] =
    useState(false);

  const getReconciliationStatusIcon = () => {
    if (status === ReconciliationStatus.RECONCILED) {
      return <StatusIcon type={IconStatusAndColor.OK.status} />;
    }
    return <StatusIcon type={IconStatusAndColor.WARNING.status} />;
  };

  const getUnreconciledAmount = () => {
    return formatAmountWithDecimals(unreconciledAmount || 0);
  };

  const getReconciledAmount = () => {
    return formatAmountWithDecimals(reconciledAmount || 0);
  };

  const getPaymentMethodLabel = () => {
    switch (paymentMethod) {
      case '01':
        return (
          <>
            <StoreIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('SANTANDER_REPORTS.PAYMENT_METHOD_WINDOW')}
          </>
        );
      case '02':
        return (
          <>
            <AccountBalanceWalletIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('SANTANDER_REPORTS.PAYMENT_METHOD_TRANSFER')}
          </>
        );
      default:
        return paymentMethod || t('COMMON.NOT_AVAILABLE');
    }
  };

  const getMoreMenuOptions = useCallback(() => {
    const options = [];

    if (status !== ReconciliationStatus.RECONCILED) {
      options.push({
        name: MenuOptions.RECONCILE,
        label: 'SANTANDER_REPORTS.RECONCILE',
        icon: <CompareArrowsIcon />
      });
      options.push({
        name: MenuOptions.CREATE_UPFRONT_RENT,
        label: 'SANTANDER_REPORTS.CREATE_UPFRONT_RENT',
        icon: <RequestQuoteIcon />
      });
        options.push({
          name: MenuOptions.CREATE_CAPITAL_CONTRIBUTION,
          label: 'SANTANDER_REPORTS.CREATE_CAPITAL_CONTRIBUTION',
          icon: <AddCircleOutlineIcon />
        });
    }

    return options;
  }, [status]);

  const handleMenuOptionClick = useCallback(optionName => {
    if (optionName === MenuOptions.RECONCILE) {
      setReconcileModalOpen(true);
    }
    if (optionName === MenuOptions.CREATE_UPFRONT_RENT) {
      setUpfrontRentModalOpen(true);
    }
    if (optionName === MenuOptions.CREATE_CAPITAL_CONTRIBUTION) {
      setCreateContributionModalOpen(true);
    }
  }, []);

  const handleCloseReconcileModal = useCallback(() => {
    setReconcileModalOpen(false);
  }, []);

  const handleCloseUpfrontRentModal = useCallback(() => {
    setUpfrontRentModalOpen(false);
  }, []);

  const otherPayments = (allPayments || []).filter(
    payment =>
      payment.id !== id &&
      payment.client?.id === client.id &&
      payment.status !== ReconciliationStatus.RECONCILED
  );

  const paymentData = {
    id,
    client,
    amount,
    incomeClabe,
    paymentMethod,
    unreconciledPayments,
    reconciledAmount,
    unreconciledAmount,
    status
  };

  return (
    <>
      <TableRow hover align="left">
        <TableCell align="left">
          <Link to={`/clients/detail/${client.id}/accounting`}>{client.fullname}</Link>
        </TableCell>
        <TableCell align="center">
          {formatAmountWithDecimals(amount)}
        </TableCell>
        <TableCell align="center">{incomeClabe}</TableCell>
        <TableCell align="center">{getPaymentMethodLabel()}</TableCell>
        <TableCell align="center">
          <Chip
            icon={getReconciliationStatusIcon()}
            label={
              status === ReconciliationStatus.RECONCILED
                ? t('COMMON.COMPLETED')
                : t('COMMON.SINGULAR_PENDING')
            }
            sx={{ backgroundColor: 'transparent' }}
            size="small"
          />
        </TableCell>
        <TableCell align="center">{getReconciledAmount()}</TableCell>
        <TableCell align="center">{getUnreconciledAmount()}</TableCell>
        <TableCell align="right">
          <MoreMenu
            deleteDisabled
            editDisabled
            disabled={status === ReconciliationStatus.RECONCILED}
            key={id}
            options={getMoreMenuOptions()}
            handleClickOption={handleMenuOptionClick}
          />
        </TableCell>
      </TableRow>

      <ReconcilePaymentModal
        open={reconcileModalOpen}
        onClose={handleCloseReconcileModal}
        paymentData={paymentData}
        reportId={reportId}
      />

      <CreateUpfrontRentModal
        open={upfrontRentModalOpen}
        onClose={handleCloseUpfrontRentModal}
        paymentData={paymentData}
        reportId={reportId}
      />

      <CreateCapitalContributionModal
        open={createContributionModalOpen}
        onClose={() => setCreateContributionModalOpen(false)}
        paymentData={paymentData}
        otherPayments={otherPayments}
        reportId={reportId}
      />
    </>
  );
};

SantanderPaymentDetailsRow.propTypes = {
  id: PropTypes.number.isRequired,
  client: PropTypes.shape({
    id: PropTypes.number.isRequired,
    fullname: PropTypes.string.isRequired
  }).isRequired,
  amount: PropTypes.number.isRequired,
  incomeClabe: PropTypes.string.isRequired,
  paymentMethod: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  unreconciledPayments: PropTypes.number.isRequired,
  reconciledAmount: PropTypes.number.isRequired,
  unreconciledAmount: PropTypes.number.isRequired,
  reportId: PropTypes.string.isRequired,
  allPayments: PropTypes.array
};

export default SantanderPaymentDetailsRow;
